import { CoreCustomFieldValueDTO } from "../../types/custom_fields/CoreCustomFieldValueDTO";
import { CoreCustomField, CoreCustomFieldType, CoreCustomFieldValueAllIncludes } from "../../queries/locations";
import { makeFullFormattedAddressWithObject } from "../functions";

/**
 * Extra options for use in displaying location fields
 */
export type FieldDisplayOptions = {
  agencyCountry?: string;
};

export function getCustomFieldValueFromElasticSearchDTO(
  elasticSearchDTO: {
    custom_field_id: number;
    custom_field_name: string;
    custom_field_type: CoreCustomFieldType;
    custom_field_value_id: number;
    value_keyword: string | null;
    value_text: string | null;
    value_text_array: { value: string }[];
    value_boolean: boolean | null;
    value_number: number | null;
    value_datetime: string | null;
    value_address?: {
      attn: string;
      address: string;
      address2: string;
      city: string;
      state: string;
      zip: string;
    } | null;
  } | null,
) {
  if (!elasticSearchDTO) return null;

  const inputLookup = {
    Address: elasticSearchDTO.value_address,
    Checkbox: elasticSearchDTO.value_boolean,
    Currency: elasticSearchDTO.value_number,
    Date: elasticSearchDTO.value_datetime ? new Date(elasticSearchDTO.value_datetime) : null,
    DateTime: elasticSearchDTO.value_datetime ? new Date(elasticSearchDTO.value_datetime) : null,
    Dropdown: elasticSearchDTO.value_text,
    DropdownMultiSelect: elasticSearchDTO.value_text_array.map(({ value }) => {
      return { corePicklistItem: { value } };
    }),
    Email: elasticSearchDTO.value_text,
    Number: elasticSearchDTO.value_number,
    Percent: elasticSearchDTO.value_number,
    PhoneNumber: elasticSearchDTO.value_text,
    Text: elasticSearchDTO.value_text,
    TextArea: elasticSearchDTO.value_text,
    Time: elasticSearchDTO.value_datetime ? new Date(elasticSearchDTO.value_datetime) : null,
    URL: elasticSearchDTO.value_text,
    LookUp: undefined,
  };

  return inputLookup[elasticSearchDTO.custom_field_type];
}

export function getCustomFieldValueFromDTO(customField: CoreCustomField, customFieldValue: CoreCustomFieldValueDTO) {
  if (!customFieldValue) return null;

  const inputLookup = {
    Address: customFieldValue.valueAddress,
    Checkbox: customFieldValue.valueBoolean,
    Currency: customFieldValue.valueInt,
    Date: customFieldValue.valueDatetime,
    DateTime: customFieldValue.valueDatetime,
    Dropdown: customFieldValue.valuePicklistItemId,
    DropdownMultiSelect: customFieldValue.valuePicklistItemIds,
    Email: customFieldValue.valueString,
    Number: customFieldValue.valueFloat,
    Percent: customFieldValue.valueFloat,
    PhoneNumber: customFieldValue.valueString,
    Text: customFieldValue.valueString,
    TextArea: customFieldValue.valueString,
    Time: customFieldValue.valueDatetime,
    URL: customFieldValue.valueString,
    LookUp: undefined,
  };

  return inputLookup[customField.type];
}

export function getCustomFieldValue(
  customField: CoreCustomField,
  customFieldValue: {
    valueAddress?: any;
    valueBoolean?: any;
    valueInt?: any;
    valueDatetime?: any;
    valuePicklistItemId?: any;
    valueManyPicklistItems?: any;
    valueString?: any;
    valueFloat?: any;
  } | null,
) {
  if (!customFieldValue) return null;

  const inputLookup = {
    Address: customFieldValue.valueAddress,
    Checkbox: customFieldValue.valueBoolean,
    Currency: customFieldValue.valueInt,
    Date: customFieldValue.valueDatetime,
    DateTime: customFieldValue.valueDatetime,
    Dropdown: customFieldValue.valuePicklistItemId,
    DropdownMultiSelect: customFieldValue.valueManyPicklistItems?.map(
      (valueManyPicklistItems: any) => valueManyPicklistItems.corePicklistItem.id,
    ),
    Email: customFieldValue.valueString,
    Number: customFieldValue.valueFloat,
    Percent: customFieldValue.valueFloat,
    PhoneNumber: customFieldValue.valueString,
    Text: customFieldValue.valueString,
    TextArea: customFieldValue.valueString,
    Time: customFieldValue.valueDatetime,
    URL: customFieldValue.valueString,
    LookUp: undefined,
  };

  return inputLookup[customField.type];
}

const inputLookup = {
  Address: (value: any) => makeFullFormattedAddressWithObject(value),
  Checkbox: (value: any) => {
    if (value === null) return null;
    return value ? "Yes" : "No";
  },
  Currency: (value: any) => (value ? "$" + (value / 100).toFixed(2) : null),
  Date: (value: any) =>
    value?.toLocaleDateString("en-us", {
      month: "short",
      year: "numeric",
      day: "numeric",
    }),
  DateTime: (value: any) =>
    value?.toLocaleDateString("en-us", {
      month: "short",
      year: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }),
  Dropdown: (value: any) => value,
  DropdownMultiSelect: (value: any) =>
    value
      ?.map((value: any) => {
        return value.corePicklistItem.value;
      })
      .join(" • "),
  Email: (value: any) => value,
  Number: (value: any) => value,
  Percent: (value: any) => value,
  PhoneNumber: (value: any) => value,
  Text: (value: any) => value,
  // If text area exceeds length limit, crop and append "..."
  TextArea: (value: any) =>
    value ? value?.substring(0, 50) + (value?.length === value?.substring(0, 50).length ? "" : "...") : null,
  Time: (value: any) =>
    value?.toLocaleTimeString("en-us", {
      hour: "numeric",
      minute: "2-digit",
    }),
  URL: (value: any) => value,
  LookUp: () => null, //TODO: Implement this
};

export function getStandardValueAsString(value: any, type: keyof typeof inputLookup, nullReplacement = "--"): string {
  if (!inputLookup[type]) return nullReplacement;
  return inputLookup[type](value)?.toString() ?? nullReplacement;
}

export function getCustomFieldValueAsString(
  customField: CoreCustomField | null,
  customFieldValue: CoreCustomFieldValueAllIncludes | null,
  options?: FieldDisplayOptions,
): string {
  if (!customFieldValue || !customField) return "--";

  const inputLookup = {
    Address: () => {
      const addressObj = customFieldValue.valueAddress;
      const showFlag = !!options?.agencyCountry && addressObj?.countryId !== options.agencyCountry;
      return makeFullFormattedAddressWithObject({
        address: addressObj?.address,
        address2: addressObj?.address2,
        city: addressObj?.city,
        state: addressObj?.state,
        zip: addressObj?.zip,
        attn: addressObj?.attn,
        countryId: showFlag ? addressObj?.countryId : null,
      });
    },
    Checkbox: () => {
      if (customFieldValue.valueBoolean === null) return "--";
      return customFieldValue.valueBoolean ? "Yes" : "No";
    },
    Currency: () => (customFieldValue.valueInt ? "$" + (customFieldValue.valueInt / 100).toFixed(2) : null),
    Date: () => {
      if (!customFieldValue.valueDatetime) return null;
      return new Date(customFieldValue.valueDatetime).toLocaleDateString("en-us", {
        month: "short",
        year: "numeric",
        day: "numeric",
      });
    },
    DateTime: () => {
      if (!customFieldValue.valueDatetime) return null;
      return new Date(customFieldValue.valueDatetime).toLocaleDateString("en-us", {
        month: "short",
        year: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    },
    Dropdown: () => customFieldValue.valuePicklistItem?.value,
    DropdownMultiSelect: () =>
      customFieldValue.valueManyPicklistItems
        ?.map((value) => {
          return value.corePicklistItem.value;
        })
        .join(" • "),
    Email: () => customFieldValue.valueString,
    Number: () => customFieldValue.valueFloat,
    Percent: () => customFieldValue.valueFloat,
    PhoneNumber: () => customFieldValue.valueString,
    Text: () => customFieldValue.valueString,
    // If text area exceeds length limit, crop and append "..."
    TextArea: () =>
      customFieldValue.valueString
        ? customFieldValue.valueString?.substring(0, 50) +
          (customFieldValue.valueString?.length === customFieldValue.valueString?.substring(0, 50).length ? "" : "...")
        : null,
    Time: () => {
      if (!customFieldValue.valueDatetime) return null;
      return new Date(customFieldValue.valueDatetime).toLocaleDateString("en-us", {
        hour: "numeric",
        minute: "2-digit",
      });
    },
    URL: () => customFieldValue.valueString,
    LookUp: () => "--",
  };

  if (!inputLookup[customField.type]) return "--";
  return inputLookup[customField.type]()?.toString() ?? "--";
}
